import i18next from 'i18next';
import { ReactNode } from 'react';

import { Bank, FiatCurrencyCode } from 'enums';

export type PaymentRuleOptions = {
  isTSBP?: boolean;
  isSBP?: boolean;
  isSIM?: boolean;
  isCard2Card?: boolean;
  isTCard2Card?: boolean;
  customerBank?: string;
  customerBankName?: string;
  requisitesCountry?: string;
  fiatCurrency?: string;
  requisitesBank?: string;
  requisitesBankName?: string;
  sameBank?: boolean;
};

type PaymentRuleRenderFn = (options: PaymentRuleOptions) => ReactNode;
type PaymentRule = {
  isMatch: (options: PaymentRuleOptions) => boolean;
  render: PaymentRuleRenderFn;
};

const t = (key: string, options?: Record<string, string | undefined>) =>
  i18next.t(`components.payment_details.${key}`, {
    defaultValue: key,
    ...options,
  });

const DetailsList = ({
  rows = [],
  showOtherBanksRule,
  options = {},
  children,
}: {
  rows?: string[];
  showOtherBanksRule?: boolean;
  options?: PaymentRuleOptions;
  children?: React.ReactNode;
}) => (
  <ul>
    {rows.map((ruleText, index) => (
      <li
        key={index}
        className="tw-flex tw-items-center tw-mb-2 tw-text-primary last-of-type:tw-mb-0"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-basis-9 tw-h-9 tw-mr-4 tw-font-bold tw-text-layout-accent tw-rounded-full tw-bg-layout-accent-alt">
          {(++index).toString().padStart(2, '0')}
        </div>
        {ruleText}
      </li>
    ))}
    {options?.sameBank ? (
      <li className="tw-text-center tw-text-red-500 tw-font-bold tw-mt-4 tw-p-3 tw-border-2 tw-rounded-xl tw-border-red-500">
        {t('rules.sbp_same_bank', {
          bank: options?.requisitesBankName || options?.requisitesBank,
        })}
      </li>
    ) : (
      showOtherBanksRule && (
        <li className="tw-text-primary tw-font-bold tw-mt-4">
          {t('rules.other_banks')}
        </li>
      )
    )}
    {children}
  </ul>
);

export const DEFAULT_PAYMENT_RULE_RENDER: PaymentRuleRenderFn = (options) => (
  <DetailsList
    rows={[t('rules.rule_one'), t('rules.rule_two'), t('rules.rule_three')]}
    options={options}
  />
);

export const PAYMENT_RULES: PaymentRule[] = [
  {
    isMatch: ({ isTSBP, requisitesBank }) =>
      !isTSBP && requisitesBank === Bank.Namba,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tbank_kgs.rule_one'),
          t('rules_tbank_kgs.rule_two'),
          t('rules_tbank_kgs.rule_three'),
          t('rules_tbank_kgs.rule_four'),
          t('rules_tbank_kgs.rule_five'),
        ]}
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isSBP, requisitesBank, customerBank }) =>
      requisitesBank === Bank.Amra && !!isSBP && customerBank === Bank.Tinkoff,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_sbp_tinkoff_amra.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_sbp_tinkoff_amra.rule_two'),
          t('rules_sbp_tinkoff_amra.rule_three'),
        ]}
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isSBP, requisitesBank, customerBank }) =>
      (requisitesBank === Bank.Amobile ||
        requisitesBank === Bank.AmobileAlternate) &&
      !!isSBP &&
      customerBank === Bank.Tinkoff,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_sbp_tinkoff_amobile.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_sbp_tinkoff_amobile.rule_two'),
          t('rules_sbp_tinkoff_amobile.rule_three'),
        ]}
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isSBP, requisitesBank, customerBank }) =>
      (requisitesBank === Bank.Amobile ||
        requisitesBank === Bank.AmobileAlternate) &&
      !!isSBP &&
      customerBank === Bank.Sberbank,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_sbp_sberbank_amobile.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_sbp_sberbank_amobile.rule_two'),
          t('rules_sbp_sberbank_amobile.rule_three'),
          t('rules_sbp_sberbank_amobile.rule_four'),
        ]}
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank, fiatCurrency }) =>
      !!isTSBP &&
      customerBank === Bank.VTB &&
      fiatCurrency === FiatCurrencyCode.TJS,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_vtb_tjs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_vtb_tjs.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tsbp_vtb_tjs.rule_three'),
          t('rules_tsbp_vtb_tjs.rule_four'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank, fiatCurrency }) =>
      !!isTSBP &&
      customerBank === Bank.Tinkoff &&
      fiatCurrency === FiatCurrencyCode.KGS,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_tinkoff_kgs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_tinkoff_kgs.rule_two'),
          t('rules_tsbp_tinkoff_kgs.rule_three'),
          t('rules_tsbp_tinkoff_kgs.rule_four'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank, fiatCurrency }) =>
      !!isTSBP &&
      customerBank === Bank.Tinkoff &&
      fiatCurrency === FiatCurrencyCode.TJS,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_tinkoff_tjs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_tinkoff_tjs.rule_two'),
          t('rules_tsbp_tinkoff_tjs.rule_three'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank }) =>
      !!isTSBP && customerBank === Bank.Tinkoff,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_tinkoff.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_tinkoff.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tsbp_tinkoff.rule_three'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank, fiatCurrency }) =>
      !!isTSBP &&
      customerBank === Bank.Sberbank &&
      fiatCurrency === FiatCurrencyCode.AZN,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_sberbank_azn.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_sberbank_azn.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tsbp_sberbank_azn.rule_three'),
          t('rules_tsbp_sberbank_azn.rule_four'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank, fiatCurrency }) =>
      !!isTSBP &&
      customerBank === Bank.Sberbank &&
      fiatCurrency === FiatCurrencyCode.TJS,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_sberbank_tjs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_sberbank_tjs.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tsbp_sberbank_tjs.rule_three'),
          t('rules_tsbp_sberbank_tjs.rule_four'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTSBP, customerBank }) =>
      !!isTSBP && customerBank === Bank.Sberbank,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_sberbank.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tsbp_sberbank.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tsbp_sberbank.rule_three'),
          t('rules_tsbp.rule_last'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTCard2Card, fiatCurrency, customerBank }) =>
      !!isTCard2Card &&
      fiatCurrency === FiatCurrencyCode.KGS &&
      customerBank === Bank.Sberbank,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tcard2card_sberbank_kgs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tcard2card_sberbank_kgs.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tcard2card_sberbank_kgs.rule_three'),
          t('rules_tcard2card_sberbank_kgs.rule_four'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTCard2Card, fiatCurrency, customerBank }) =>
      !!isTCard2Card &&
      fiatCurrency === FiatCurrencyCode.KGS &&
      customerBank === Bank.VTB,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tcard2card_vtb_kgs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tcard2card_vtb_kgs.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tcard2card_vtb_kgs.rule_three'),
          t('rules_tcard2card_vtb_kgs.rule_four'),
          t('rules_tcard2card_vtb_kgs.rule_five'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTCard2Card, fiatCurrency, customerBank }) =>
      !!isTCard2Card &&
      fiatCurrency === FiatCurrencyCode.KGS &&
      customerBank === Bank.Tinkoff,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tcard2card_tinkoff_kgs.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tcard2card_tinkoff_kgs.rule_two'),
          t('rules_tcard2card_tinkoff_kgs.rule_three'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isTCard2Card, fiatCurrency, customerBank }) =>
      !!isTCard2Card &&
      fiatCurrency === FiatCurrencyCode.ABH &&
      customerBank === Bank.Tinkoff,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tcard2card_tinkoff_abh.rule_one', {
            bank: options?.customerBankName,
          }),
          t('rules_tcard2card_tinkoff_abh.rule_two'),
          t('rules_tcard2card_tinkoff_abh.rule_three'),
        ]}
        showOtherBanksRule
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isCard2Card, fiatCurrency, requisitesBank }) =>
      !!isCard2Card &&
      requisitesBank === Bank.SberAbh &&
      fiatCurrency === FiatCurrencyCode.RUB,
    render: (options) => (
      <DetailsList
        rows={[
          t('rules_tsbp_sberabh_rub.rule_one'),
          t('rules_tsbp_sberabh_rub.rule_two', {
            country: options?.requisitesCountry,
          }),
          t('rules_tsbp_sberabh_rub.rule_three'),
          t('rules_tsbp_sberabh_rub.rule_four', {
            bank: options?.requisitesBankName,
          }),
          t('rules_tsbp_sberabh_rub.rule_five'),
        ]}
        options={options}
      />
    ),
  },
  {
    isMatch: ({ isSIM }) => !!isSIM,
    render: (options) => (
      <DetailsList
        rows={[t('rules_sim.rule_one'), t('rules_sim.rule_two')]}
        options={options}
      />
    ),
  },
];
